import { Action } from '../types/types';
import type { ScheduledStream } from '../types/connection';
import { isStreamResponse } from '../../connection/scheduled-streams';
import { EntityType, RoomStates } from '../../types/enums';
import { EnablerStates } from '../../stores/types/session';
import { Entity } from '../../types/interfaces';
import {
	SET_CANVAS_VOLUME_BACKSTAGE,
	SET_ROOM_STATE,
	SET_ROOM_STATE_TRANSITIONING,
	SET_SCHEDULED_STREAM,
	SET_ENABLER_SHOT_ID,
	SET_ENABLER_STATE,
} from '../actions/meeting';
import type { MeetingReducerState } from '../types/meeting';

export const initialMeetingState: MeetingReducerState = {
	backstageCanvasVolume: 1,
	scheduledStream: null,
	roomStateEntity: null,
	roomState: RoomStates.showbuilder,
	roomStateTransitioning: false,
	enablerShotId: null,
	enablerState: EnablerStates.None,
};

const formatScheduledStream = (stream: any): ScheduledStream | null => {
	if (stream && isStreamResponse(stream)) {
		return {
			...stream,
			startTime: new Date(stream?.startTime),
			createdDate: new Date(stream?.createdDate),
			updatedDate: new Date(stream?.updatedDate),
		};
	}
	return null;
};

const actions: Map<any, Function> = new Map([
	[
		SET_CANVAS_VOLUME_BACKSTAGE,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			backstageCanvasVolume: action.payload,
		}),
	],
	[
		SET_SCHEDULED_STREAM,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			scheduledStream: formatScheduledStream(action.payload),
		}),
	],
	[
		EntityType.scheduledStream,
		(state: MeetingReducerState, action: Action): MeetingReducerState => {
			const scheduledStream = formatScheduledStream(
				action.payload?.entityValue?.[EntityType.scheduledStream],
			);

			return {
				...state,
				scheduledStream,
			};
		},
	],
	[
		EntityType.roomState,
		(state: MeetingReducerState, action: Action): MeetingReducerState => {
			const currentEntity = !!state.roomStateEntity;
			const roomState: number = action.payload?.entityValue?.roomState
				?? RoomStates.showbuilder;

			return {
				...state,
				roomStateEntity: action.payload,
				...(!currentEntity && {
					roomState,
				}),
			};
		},
	],
	[
		SET_ROOM_STATE,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			roomState: action.payload,
		}),
	],
	[
		SET_ROOM_STATE_TRANSITIONING,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			roomStateTransitioning: action.payload,
		}),
	],
	[
		SET_ENABLER_SHOT_ID,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			enablerShotId: action.payload,
			enablerState: EnablerStates.Thumbnail,
		}),
	],
	[
		SET_ENABLER_STATE,
		(state: MeetingReducerState, action: Action): MeetingReducerState => ({
			...state,
			enablerState: action.payload,
		}),
	],
]);

// eslint-disable-next-line default-param-last
export function MeetingReducer(state: any = initialMeetingState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
